import { c as _c } from "react/compiler-runtime";
import animationStyles from '@/components/Atoms/Animation/Animation.module.css';
import { SkeletonCard } from '@/components/Atoms/SkeletonCard/SkeletonCard';
import classNames from '@/utils/classNames';
import type { ComponentProps, CSSProperties, HTMLAttributes, OlHTMLAttributes, ReactNode } from 'react';
import { Children } from 'react';
import type { FCC } from 'react-fc';
import styles from './CardGrid.module.css';
interface BaseCardGridProps {
  /**
   * The minimum width of each card in the grid. The grid will wrap if cards would become narrower than this width.
   * @default 304px
   */
  min?: string;
  /**
   * Should the items animate into existence?
   */
  animate?: boolean;
  /**
   * Use auto-fit algorithm instead of auto-fill
   */
  autoFit?: boolean;
}
interface DivCardGridProps extends BaseCardGridProps, HTMLAttributes<HTMLDivElement> {
  list: false;
}
interface UlCardGridProps extends BaseCardGridProps, HTMLAttributes<HTMLUListElement> {
  list?: true; // Optional to allow default value
}
interface OlCardGridProps extends BaseCardGridProps, OlHTMLAttributes<HTMLOListElement> {
  list: 'ol';
}
type CardGridProps = DivCardGridProps | UlCardGridProps | OlCardGridProps;

/**
 * A fluid container for a grid of content cards.
 * Specify the card's min/max widths and the grid will fit as many as possible in the available space.
 */
export const CardGrid: FCC<CardGridProps> = t0 => {
  const $ = _c(40);
  let children;
  let className;
  let min;
  let rest;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      children,
      autoFit: t1,
      animate: t2,
      list: t3,
      min,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = min;
    $[4] = rest;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    children = $[1];
    className = $[2];
    min = $[3];
    rest = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  const autoFit = t1 === undefined ? false : t1;
  const animate = t2 === undefined ? false : t2;
  const list = t3 === undefined ? true : t3;
  let t4;
  if ($[8] !== children || $[9] !== list) {
    let t5;
    if ($[11] !== list) {
      t5 = child => {
        if (!child) {
          return null;
        }
        if (list) {
          return <li className={styles.item}>{child}</li>;
        }
        return <div className={styles.item}>{child}</div>;
      };
      $[11] = list;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = Children.map(children, t5);
    $[8] = children;
    $[9] = list;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const childComponents = t4;
  let t5;
  if ($[13] !== min) {
    t5 = {
      "--_min": min
    };
    $[13] = min;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = t5 as CSSProperties;
  let t7;
  if ($[15] !== animate || $[16] !== autoFit || $[17] !== className) {
    t7 = classNames(styles.grid, {
      [animationStyles.fadeChildren]: animate,
      [styles.autoFit]: autoFit
    }, className);
    $[15] = animate;
    $[16] = autoFit;
    $[17] = className;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = {
      style: t6,
      className: t7
    };
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const baseProps = t8;
  if (list === "ol") {
    const t9 = rest as Omit<OlCardGridProps, 'list'>;
    let olRest;
    if ($[22] !== t9) {
      const {
        min: _,
        ...t10
      } = t9;
      olRest = t10;
      $[22] = t9;
      $[23] = olRest;
    } else {
      olRest = $[23];
    }
    let t10;
    if ($[24] !== baseProps || $[25] !== childComponents || $[26] !== olRest) {
      t10 = <ol {...baseProps} {...olRest}>{childComponents}</ol>;
      $[24] = baseProps;
      $[25] = childComponents;
      $[26] = olRest;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    return t10;
  }
  if (list) {
    const t9 = rest as Omit<UlCardGridProps, 'list'>;
    let ulRest;
    if ($[28] !== t9) {
      const {
        min: __0,
        ...t10
      } = t9;
      ulRest = t10;
      $[28] = t9;
      $[29] = ulRest;
    } else {
      ulRest = $[29];
    }
    let t10;
    if ($[30] !== baseProps || $[31] !== childComponents || $[32] !== ulRest) {
      t10 = <ul {...baseProps} {...ulRest}>{childComponents}</ul>;
      $[30] = baseProps;
      $[31] = childComponents;
      $[32] = ulRest;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    return t10;
  }
  const t9 = rest as Omit<DivCardGridProps, 'list'>;
  let divRest;
  if ($[34] !== t9) {
    const {
      min: __1,
      ...t10
    } = t9;
    divRest = t10;
    $[34] = t9;
    $[35] = divRest;
  } else {
    divRest = $[35];
  }
  let t10;
  if ($[36] !== baseProps || $[37] !== childComponents || $[38] !== divRest) {
    t10 = <div {...baseProps} {...divRest}>{childComponents}</div>;
    $[36] = baseProps;
    $[37] = childComponents;
    $[38] = divRest;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return t10;
};
export const CardGridSkeleton: FCC<{
  count?: number;
  autoFit?: ComponentProps<typeof CardGrid>['autoFit'];
} & ComponentProps<typeof SkeletonCard>> = t0 => {
  const $ = _c(12);
  let autoFit;
  let rest;
  let t1;
  if ($[0] !== t0) {
    ({
      count: t1,
      autoFit,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = autoFit;
    $[2] = rest;
    $[3] = t1;
  } else {
    autoFit = $[1];
    rest = $[2];
    t1 = $[3];
  }
  const count = t1 === undefined ? 12 : t1;
  let t2;
  if ($[4] !== count) {
    t2 = Array.from({
      length: count
    });
    $[4] = count;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== rest || $[7] !== t2) {
    t3 = t2.map((_, index) => <SkeletonCard key={index} {...rest} />);
    $[6] = rest;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const skeletons = t3;
  let t4;
  if ($[9] !== autoFit || $[10] !== skeletons) {
    t4 = <CardGrid aria-label="Loading content..." list={false} autoFit={autoFit}>{skeletons}</CardGrid>;
    $[9] = autoFit;
    $[10] = skeletons;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};